import { acceptHMRUpdate, defineStore } from 'pinia'
import type {
  ItemType,
  ShoppingCartItemType,
  ShoppingCartItemWithPricesType,
  ItemRasStatus
} from '@/types/item.type'
import type { PosItemType } from '@/types/pos-item.type'

interface State {
  items: ItemType[]
  itemsForInventory: ItemType[]
  itemsWithPrices: ShoppingCartItemWithPricesType[]
  itemRasStatuses: ItemRasStatus[]
  /**
   * pos/inventory item with a match_confidence
   * via /pos-catalog/items
   */
  catalog: PosItemType[]
  editItemId: number
  newItem: {
    type: string
    typeId: number
    vipProductId?: string
  }
}

export const useItemsStore = defineStore('items', {
  state: (): State => {
    return {
      items: [], // items list returned by getItems
      itemsForInventory: [],
      itemsWithPrices: [], // items with prices
      itemRasStatuses: [],
      catalog: [],
      editItemId: 0,
      newItem: {
        type: '',
        typeId: 0,
        vipProductId: undefined
      }
    }
  },
  actions: {
    async getItems(query: Record<string, any> = {}) {
      const items: ItemType[] = await $.get('/items', query)
      this.items = items
    },
    async getItemsForInventory(query: Record<string, any> = {}) {
      const items: ItemType[] = await $.get('/items/items-for-inventory', query)
      this.itemsForInventory = items
    },
    async fetchItemRasStatuses(query: Record<string, any> = {}) {
      const { items }: { items: ItemRasStatus[]; success: boolean } = await $.post(
        '/items/ras-status',
        query
      )
      this.itemRasStatuses = items
    },
    async getDetailedCatalogItems() {
      const items: PosItemType[] = await $.get('/pos-catalog/items', { detailed: true })
      this.catalog = items
    },
    setDetailedCatalogItems(items: PosItemType[]) {
      this.catalog = items
    },
    setEditItemId(id: number) {
      this.editItemId = id
    },
    async getItemPrices(cartItems: ShoppingCartItemType[]) {
      const itemsWithPrices: ShoppingCartItemWithPricesType[] = await $.post('/items/prices', {
        items: cartItems
      })
      this.itemsWithPrices = itemsWithPrices
    },
    async getItemPrice(item: ShoppingCartItemType) {
      const itemsWithPrices: ShoppingCartItemWithPricesType[] = await $.post('/items/prices', {
        items: [item]
      })
      return itemsWithPrices[0]
    },
    setNewItemTypeId(typeId: number) {
      this.newItem.typeId = typeId
    },
    setNewItemVipProductId(vipProductId: string) {
      this.newItem.vipProductId = vipProductId
    },
    setNewItemType(type: string) {
      this.newItem.type = type
    }
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useItemsStore, import.meta.hot))
}
